import React, { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setInView(true), io.disconnect())),
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

const reveal = (inView, delay = 0) => ({
  opacity: inView ? 1 : 0,
  transform: inView ? "translateY(0)" : "translateY(14px)",
  transition: `opacity 480ms ease ${delay}ms, transform 480ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
});

const muted = "rgba(26,26,25,0.55)";
const body = "rgba(26,26,25,0.78)";

export default function About() {
  const [ref, inView] = useInView();

  return (
    <main
      ref={ref}
      className="px-6 sm:px-10 pt-20 sm:pt-28 pb-28 sm:pb-36"
      data-testid="about-page"
    >
      <div className="mx-auto" style={{ maxWidth: "720px" }}>
        <p
          className="tracking-[0.32em] uppercase text-[11px]"
          style={{ color: muted, ...reveal(inView, 0) }}
          data-testid="about-eyebrow"
        >
          A Study in Simplicity
        </p>

        <h1
          className="font-display mt-10 sm:mt-12 leading-[1.05]"
          style={{
            fontSize: "clamp(2.25rem, 4.4vw, 3.75rem)",
            letterSpacing: "-0.005em",
            fontWeight: 400,
            ...reveal(inView, 80),
          }}
          data-testid="about-heading"
        >
          When nothing is excessive,
          <br />
          <span className="italic">every detail matters.</span>
        </h1>

        <div
          className="mt-16 sm:mt-20 space-y-7 sm:space-y-8 text-base sm:text-[17px] leading-[1.9]"
          style={{ color: body, ...reveal(inView, 220) }}
          data-testid="about-body"
        >
          <p
            className="font-display"
            style={{
              fontSize: "clamp(1.25rem, 1.7vw, 1.5rem)",
              lineHeight: 1.55,
              letterSpacing: "-0.005em",
              color: "#1A1A19",
            }}
            data-testid="about-opening"
          >
            Tamoré was founded on the belief that simplicity deserves attention.
          </p>

          <p>
            In a world of constant trends and endless newness, we choose a more considered
            approach — creating pieces that feel timeless, effortless, and enduring.
          </p>

          <p>
            Every design begins with restraint. Details are added thoughtfully. Nothing is
            included without purpose. The process is guided by a simple question: what remains
            when everything unnecessary is removed?
          </p>

          <p>
            The answer is found in thoughtful construction, refined proportions, and pieces
            designed to move naturally through everyday life. Pieces that feel as relevant
            tomorrow as they do today.
          </p>
        </div>
      </div>
    </main>
  );
}
